import { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { depositAPI } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowDownToLine, Upload, CreditCard, Building2, QrCode, Smartphone, Copy, Check, Clock, ChevronDown, FileText } from 'lucide-react';
import { toast } from 'sonner';

const PAYMENT_MODES = ['QR Code', 'PhonePe', 'PayTM', 'GPay', 'BHIM UPI', 'Bank Transfer', 'NEFT', 'RTGS', 'IMPS Using IFSC'];
const formatCurrency = (v) => v == null ? '$0.00' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v);

export default function DepositPage() {
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [form, setForm] = useState({ amount_inr: '', payment_mode: '', transaction_id: '' });
  const [screenshot, setScreenshot] = useState(null);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);

  useEffect(() => {
    depositAPI.getPaymentInfo().then(r => setPaymentInfo(r.data)).catch(() => {});
    depositAPI.getHistory().then(r => setDeposits(r.data)).catch(() => {});
  }, []);

  const rate = paymentInfo?.usd_to_inr_rate || 91;
  const amountUSD = form.amount_inr ? (parseFloat(form.amount_inr) / rate).toFixed(2) : '0.00';
  const isBankMode = ['Bank Transfer', 'NEFT', 'RTGS', 'IMPS Using IFSC'].includes(form.payment_mode);
  const isQRMode = form.payment_mode === 'QR Code';
  const isUPIMode = ['PhonePe', 'PayTM', 'GPay', 'BHIM UPI'].includes(form.payment_mode);

  const copyText = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleSubmit = async () => {
    if (!form.amount_inr || parseFloat(form.amount_inr) <= 0) { toast.error('Enter a valid amount'); return; }
    if (!form.payment_mode) { toast.error('Select a payment mode'); return; }
    if (!form.transaction_id || form.transaction_id.trim().length < 4) { toast.error('Enter a valid Transaction ID'); return; }
    if (!agreeTerms) { toast.error('You must agree to the terms'); return; }
    setLoading(true);
    try {
      const res = await depositAPI.submit({ amount_inr: parseFloat(form.amount_inr), payment_mode: form.payment_mode, transaction_id: form.transaction_id.trim() });
      // Upload screenshot if available
      if (screenshot && res.data.id) {
        const fd = new FormData();
        fd.append('file', screenshot);
        await depositAPI.uploadScreenshot(res.data.id, fd);
      }
      toast.success('Deposit request submitted! Waiting for admin approval.');
      setForm({ amount_inr: '', payment_mode: '', transaction_id: '' });
      setScreenshot(null);
      setAgreeTerms(false);
      depositAPI.getHistory().then(r => setDeposits(r.data)).catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit deposit');
    } finally { setLoading(false); }
  };

  const statusColor = { pending: 'text-amber-400 bg-amber-500/15', approved: 'text-emerald-400 bg-emerald-500/15', rejected: 'text-red-400 bg-red-500/15' };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="deposit-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-emerald-500/15 flex items-center justify-center">
            <ArrowDownToLine className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-lg font-semibold font-['Outfit'] text-[var(--text-primary)]">Add Funds</h1>
            <p className="text-xs text-[var(--text-secondary)]">Deposit money to your trading wallet</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Deposit Form */}
          <div className="lg:col-span-5 space-y-4">
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-5 space-y-4" data-testid="deposit-form">
              <h2 className="text-sm font-semibold text-[var(--text-primary)]">Deposit Details</h2>

              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--text-secondary)]">Fund Amount (INR)</Label>
                <Input type="number" step="any" placeholder="Enter amount in INR" value={form.amount_inr}
                  onChange={(e) => setForm({ ...form, amount_inr: e.target.value })} data-testid="deposit-amount"
                  className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono h-11" />
                {form.amount_inr && parseFloat(form.amount_inr) > 0 && (
                  <p className="text-xs text-emerald-400 font-mono">~ ${amountUSD} USD (Rate: ₹{rate}/USD)</p>
                )}
              </div>

              <div className="flex gap-2 flex-wrap">
                {[1000, 5000, 10000, 50000].map(amt => (
                  <button key={amt} onClick={() => setForm({ ...form, amount_inr: String(amt) })}
                    className="px-3 py-1.5 rounded text-xs font-mono bg-[var(--hover-bg)] border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
                    ₹{amt.toLocaleString()}
                  </button>
                ))}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--text-secondary)]">Payment Mode</Label>
                <div className="relative">
                  <select value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} data-testid="deposit-payment-mode"
                    className="w-full px-3 py-2.5 bg-[var(--bg-primary)] border border-[var(--border-subtle)] rounded text-sm text-[var(--text-primary)] appearance-none focus:outline-none focus:border-green-500">
                    <option value="">Select payment mode...</option>
                    {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--text-secondary)]">Transaction ID / UTR Number</Label>
                <Input type="text" placeholder="Enter UTR / Transaction ID" value={form.transaction_id}
                  onChange={(e) => setForm({ ...form, transaction_id: e.target.value })} data-testid="deposit-txn-id"
                  className="bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-primary)] font-mono h-11" />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs text-[var(--text-secondary)]">Upload Payment Screenshot</Label>
                <label className="flex items-center gap-3 px-4 py-3 bg-[var(--bg-primary)] border border-dashed border-[var(--border-subtle)] rounded cursor-pointer hover:border-green-500/40 transition-colors" data-testid="deposit-screenshot-upload">
                  <Upload className="w-5 h-5 text-[var(--text-tertiary)]" />
                  <span className="text-sm text-[var(--text-secondary)]">{screenshot ? screenshot.name : 'Choose file...'}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => setScreenshot(e.target.files?.[0] || null)} />
                </label>
              </div>

              <label className="flex items-start gap-2.5 cursor-pointer" data-testid="deposit-terms">
                <input type="checkbox" checked={agreeTerms} onChange={(e) => setAgreeTerms(e.target.checked)}
                  className="mt-0.5 accent-green-500" />
                <span className="text-xs text-[var(--text-secondary)] leading-relaxed">
                  I confirm the payment details are correct and agree to the terms and conditions. I understand deposits are subject to admin verification.
                </span>
              </label>

              <button onClick={handleSubmit} disabled={loading} data-testid="deposit-submit-btn"
                className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded text-sm font-medium transition-all disabled:opacity-40">
                {loading ? 'Submitting...' : 'Submit Deposit Request'}
              </button>
            </div>
          </div>

          {/* Payment Info Cards */}
          <div className="lg:col-span-4 space-y-4">
            {/* QR Code - shown when QR Code mode is selected */}
            {paymentInfo && isQRMode && (
              <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-5 space-y-3" data-testid="qr-info-card">
                <div className="flex items-center gap-2">
                  <QrCode className="w-4 h-4 text-green-400" />
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">Scan QR Code to Pay</h3>
                </div>
                {paymentInfo.qr_code_url ? (
                  <div className="flex justify-center p-4 bg-white rounded">
                    <img src={`${process.env.REACT_APP_BACKEND_URL || ''}${paymentInfo.qr_code_url}`} alt="QR Code" className="w-52 h-52 object-contain" />
                  </div>
                ) : (
                  <div className="p-6 text-center bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                    <QrCode className="w-8 h-8 text-[var(--text-tertiary)] mx-auto mb-2" />
                    <p className="text-xs text-[var(--text-secondary)]">QR Code not yet configured by admin.</p>
                  </div>
                )}
                <p className="text-[10px] text-[var(--text-tertiary)] text-center">Scan and pay, then enter Transaction ID below</p>
              </div>
            )}

            {/* UPI Details - shown for UPI-based modes */}
            {paymentInfo && isUPIMode && (
              <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-5 space-y-3" data-testid="upi-info-card">
                <div className="flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-green-400" />
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">UPI Payment Details</h3>
                </div>
                <div className="space-y-2">
                  {(paymentInfo.upi_ids || []).map((upi, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                      <span className="text-sm font-mono text-[var(--text-primary)]">{upi}</span>
                      <button onClick={() => copyText(upi, `upi-${i}`)} className="p-1 text-[var(--text-secondary)] hover:text-green-400">
                        {copied === `upi-${i}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* UPI + QR combo - shown when no specific mode is selected yet */}
            {paymentInfo && !isQRMode && !isUPIMode && !isBankMode && (
              <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-5 space-y-3" data-testid="default-info-card">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-green-400" />
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">Payment Details</h3>
                </div>
                {paymentInfo.qr_code_url && (
                  <div className="flex justify-center p-3 bg-white rounded">
                    <img src={`${process.env.REACT_APP_BACKEND_URL || ''}${paymentInfo.qr_code_url}`} alt="QR Code" className="w-36 h-36 object-contain" />
                  </div>
                )}
                <div className="space-y-2">
                  {(paymentInfo.upi_ids || []).map((upi, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                      <span className="text-sm font-mono text-[var(--text-primary)]">{upi}</span>
                      <button onClick={() => copyText(upi, `upi-${i}`)} className="p-1 text-[var(--text-secondary)] hover:text-green-400">
                        {copied === `upi-${i}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bank Details */}
            {paymentInfo && isBankMode && (
              <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded p-5 space-y-3" data-testid="bank-info-card">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-400" />
                  <h3 className="text-sm font-semibold text-[var(--text-primary)]">Bank Transfer Details</h3>
                </div>
                {[
                  { label: 'Account Holder', value: paymentInfo.account_holder },
                  { label: 'Bank Name', value: paymentInfo.bank_name },
                  { label: 'Account Number', value: paymentInfo.account_number },
                  { label: 'IFSC Code', value: paymentInfo.ifsc_code },
                ].map((item, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 bg-[var(--bg-primary)] rounded border border-[var(--border-subtle)]">
                    <div>
                      <p className="text-[10px] text-[var(--text-tertiary)] uppercase">{item.label}</p>
                      <p className="text-sm font-mono text-[var(--text-primary)]">{item.value}</p>
                    </div>
                    <button onClick={() => copyText(item.value, `bank-${i}`)} className="p-1 text-[var(--text-secondary)] hover:text-green-400">
                      {copied === `bank-${i}` ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="p-3 bg-amber-500/5 border border-amber-500/15 rounded">
              <p className="text-xs text-amber-400 font-medium mb-1">Important</p>
              <p className="text-[11px] text-[var(--text-secondary)]">Deposits are credited after admin verification. This usually takes 5-30 minutes during business hours.</p>
            </div>
          </div>

          {/* Deposit History */}
          <div className="lg:col-span-3">
            <div className="bg-[var(--bg-surface)] border border-[var(--border-subtle)] rounded" data-testid="deposit-history">
              <div className="px-4 py-3 border-b border-[var(--border-subtle)] flex items-center gap-2">
                <Clock className="w-4 h-4 text-[var(--text-secondary)]" />
                <h3 className="text-sm font-semibold text-[var(--text-primary)]">Recent Deposits</h3>
              </div>
              {deposits.length === 0 ? (
                <div className="p-6 text-center text-xs text-[var(--text-secondary)]">No deposits yet</div>
              ) : (
                <div className="divide-y divide-[var(--divide-color)]">
                  {deposits.slice(0, 8).map((d) => (
                    <div key={d.id} className="px-4 py-3" data-testid={`deposit-item-${d.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-mono text-[var(--text-primary)]">₹{d.amount_inr?.toLocaleString()}</span>
                        <span className={`px-2 py-0.5 rounded text-[10px] ${statusColor[d.status] || ''}`}>{d.status}</span>
                      </div>
                      <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">{d.payment_mode} &middot; {new Date(d.created_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' })}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
